import Cookies from "js-cookie";
import { useEffect, useState } from "react";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ForgotPassword from "../../component/ForgotPassword";
import FromLayout from "../../component/FromLayout";
import { useAuthUserMutation } from "../../store/api/authSlice";
import { logInUser, storeTempData } from "../../store/reducer/UserReducer";
import { handleApiError } from "../../utils/dateAndTimeConversion";
import { getSubdomainRole } from "../../utils/getSubdomainRole";
import { getToken, setToken } from "../../utils/localStorageMethods";

const ROLE_CONFIG = {
  admin: {
    endpoint: "auth/admin/login",
    title: "admin_sign_in",
    showExtras: false,
    showRegister: false,
  },
  parent: {
    endpoint: "auth/login",
    title: "sign_in",
    showExtras: true,
    showRegister: false,
  },
  school: {
    endpoint: "auth/web-login",
    title: "sign_in",
    showExtras: true,
    showRegister: true,
  },
  vendor: {
    endpoint: "auth/web-login",
    title: "sign_in",
    showExtras: true,
    showRegister: true,
  },
};

// const getRedirectPath = (role, status) => {
//   if (role === "super_admin") return "/admin/dashboard";
//   if (role === "parent") {
//     if (status === "pending") return "/registration";
//     if (status === "payment_pending") return "/payment";
//     return "/parent/dashboard";`
//   }
//   if (role === "vendor") return "/vendor/dashboard";
//   if (role === "school") return "/school/dashboard";
//   return "/";
// };

const getRedirectPath = (role, status) => {
  if (role === "parent") {
    if (status === "pending") return "/registration";
    if (status === "payment_pending") return "/payment";
  }
  return "/dashboard";
};

const Login = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const token = getToken();

  const subdomainRole = getSubdomainRole();
  const config = ROLE_CONFIG[subdomainRole] ?? ROLE_CONFIG.school;

  const [show, setShow] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState({});
  const [authUser, { isLoading }] = useAuthUserMutation();

  const validateForm = () => {
    const newErrors = {};
    Object.entries(formData).forEach(([key, value]) => {
      if (!value)
        newErrors[key] = `${t("errors." + key)} ${t("errors.required")}`;
    });
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = t("invalidEmail");
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors((prev) => ({
      ...prev,
      [name]: value ? "" : `${t("errors." + name)} ${t("errors.required")}`,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    toast.dismiss();

    try {
      const response = await authUser({
        user: {
          ...formData,
          ...(subdomainRole && subdomainRole !== "admin"
            ? { expected_role: subdomainRole }
            : {}),
        },
        endPoint: config.endpoint,
      }).unwrap();

      if (response.statusCode === 200) {
        const { role, status, token } = response.data;

        if (
          role !== "parent" ||
          (status !== "pending" && status !== "payment_pending")
        ) {
          dispatch(logInUser(response.data));
        }

        if (
          role === "parent" &&
          (status === "pending" || status === "payment_pending")
        ) {
          dispatch(storeTempData(response.data));
        }

        if ((role === "vendor" || role === "school") && status === "inreview") {
          toast.error("in review");
        } else {
          toast.success(response.message);
          setToken(token);
          navigate(getRedirectPath(role, status));
        }
      }
    } catch (error) {
      handleApiError(error, t);
    }
  };

  const handleRememberMeChange = (e) => {
    setRememberMe(e.target.checked);
  };

  useEffect(() => {
    if (Cookies.get("rememberMe") === "checked") {
      setFormData({
        email: Cookies.get("email") || "",
        password: Cookies.get("password") || "",
      });
      setRememberMe(true);
    }
  }, []);

  return (
    <FromLayout title={t(config.title)} width="w-40">
      <div className="text-center mb-5">
        <p
          className="text-light"
          style={{ opacity: 0.8, maxWidth: "500px", margin: "0 auto" }}
        >
          {t("welcome_back_signin")}
        </p>
      </div>

      <div className="d-flex justify-content-center">
        <div style={{ width: "100%", maxWidth: "500px" }}>
          <form onSubmit={handleSubmit}>
            {/* Email */}
            <div className="row g-3 text-start mb-4">
              <div className="col-12">
                <label className="form-label text-white">
                  {t("email")} {t("address")}
                </label>
                <input
                  type="email"
                  className="form-control mb-1"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={`${t("enter")} ${t("your")} ${t("email")}`}
                />
                {errors.email && (
                  <span style={{ color: "red" }}>{errors.email}</span>
                )}
              </div>
            </div>

            {/* Password */}
            <div className="row g-3 text-start mb-4">
              <div className="col-12">
                <label className="form-label text-white">{t("password")}</label>
                <div className="input-group mb-1">
                  <input
                    type={isPasswordVisible ? "text" : "password"}
                    className="form-control"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder={`${t("enter")} ${t("your")} ${t("password")}`}
                  />
                </div>
                {errors.password && (
                  <span style={{ color: "red" }}>{errors.password}</span>
                )}
              </div>
            </div>

            {/* Remember Me + Forgot Password — only for non-admin */}
            {config.showExtras && (
              <div className="row g-3 text-start mb-4">
                <div className="col-12">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                      <Form.Check
                        type="checkbox"
                        id="rememberMe"
                        className="me-2"
                        checked={rememberMe}
                        onChange={handleRememberMeChange}
                      />
                      <label
                        htmlFor="rememberMe"
                        className="text-white mb-0"
                        style={{ opacity: 0.9, cursor: "pointer" }}
                      >
                        {t("remember_me")}
                      </label>
                    </div>
                    <Link
                      to="/forgot-password"
                      className="fpLink"
                      style={{
                        color: "#f0b241",
                        cursor: "pointer",
                        textDecoration: "none",
                      }}
                      onMouseEnter={(e) =>
                        (e.target.style.textDecoration = "underline")
                      }
                      onMouseLeave={(e) =>
                        (e.target.style.textDecoration = "none")
                      }
                    >
                      {t("Forgot")} {t("password")}?
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {/* Sign In Button */}
            <div className="row g-3 text-start mb-4">
              <div className="col-12">
                <button
                  type="submit"
                  className="btn btn-amber rounded-3 px-4 w-100"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <div className="spinner-border" role="status">
                      <span className="sr-only p-0"></span>
                    </div>
                  ) : (
                    t("sign_in")
                  )}
                </button>
              </div>
            </div>

            {/* Register link — only for school subdomain */}
            {config.showRegister && (
              <div className="text-center mt-4">
                <span className="text-white" style={{ opacity: 0.9 }}>
                  {t("dont_have_account")}{" "}
                </span>
                <Link
                  to="/register"
                  className="hover-link"
                  style={{
                    color: "#f0b241",
                    textDecoration: "none",
                    fontWeight: "500",
                  }}
                  onMouseEnter={(e) =>
                    (e.target.style.textDecoration = "underline")
                  }
                  onMouseLeave={(e) => (e.target.style.textDecoration = "none")}
                >
                  {t("btns.register")}
                </Link>
              </div>
            )}
          </form>
        </div>
      </div>

      {/* Forgot Password Modal */}
      <Modal show={show} onHide={() => setShow(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {t("forgot")} {t("password")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ForgotPassword handleClose={() => setShow(false)} />
        </Modal.Body>
      </Modal>
    </FromLayout>
  );
};

export default Login;
